/**
 * #249 — show a line a chat link points at (`app.py:42`): put the caret on it,
 * scroll it to the middle of the view and flash it for a moment.
 */
import { StateEffect, StateField, type Extension } from '@codemirror/state'
import { Decoration, EditorView, type DecorationSet } from '@codemirror/view'

/** How long the line stays highlighted. */
export const REVEAL_FLASH_MS = 2000

/** The flashed line's start, or null to clear it. */
const flashLine = StateEffect.define<number | null>()

const flashMark = Decoration.line({ class: 'cm-reveal-line' })

const flashField = StateField.define<DecorationSet>({
  create: () => Decoration.none,
  update(deco, tr) {
    let next = deco.map(tr.changes)
    for (const e of tr.effects) {
      if (e.is(flashLine)) next = e.value == null ? Decoration.none : Decoration.set([flashMark.range(e.value)])
    }
    return next
  },
  provide: (f) => EditorView.decorations.from(f),
})

const flashTheme = EditorView.baseTheme({
  '.cm-reveal-line': { backgroundColor: 'rgba(56, 139, 253, 0.25)', transition: 'background-color 0.4s' },
})

export const revealLineExtension: Extension = [flashField, flashTheme]

/**
 * Reveal 1-based `line` (clamped to the document) and flash it. Returns a
 * function that clears the flash early (the caller's cleanup).
 */
export function revealLine(view: EditorView, line: number): () => void {
  const doc = view.state.doc
  const target = doc.line(Math.max(1, Math.min(Math.floor(line), doc.lines)))
  view.dispatch({
    selection: { anchor: target.from },
    effects: [EditorView.scrollIntoView(target.from, { y: 'center' }), flashLine.of(target.from)],
  })
  const timer = setTimeout(() => { if (view.dom.isConnected) view.dispatch({ effects: flashLine.of(null) }) }, REVEAL_FLASH_MS)
  return () => clearTimeout(timer)
}
